import {
  AnalyticSearchRequestDto,
  DateFiltersDto,
  FiltersDto,
  KeyMetricsResponseDto,
  ShipmentFiltersDto,
  TradeLaneFiltersDto,
} from 'src/generated/services/TFinancialApi';
import IconDollar from 'src/assets/images/icons/analytic-dollar.svg';
import IconCloud from 'src/assets/images/icons/analytic-cloud.svg';
import IconWeight from 'src/assets/images/icons/analytic-weight.svg';
import IconWeightBlue from 'src/assets/images/icons/analytic-weight-blue.svg';
import IconTruck from 'src/assets/images/icons/analytic-truck.svg';
import IconReceipt from 'src/assets/images/icons/analytic-receipt.svg';
import IconWarning from 'src/assets/images/icons/analytic-warning.svg';
import IconBox from 'src/assets/images/icons/analytic-box.svg';
import IconChargesLines from 'src/assets/images/icons/analytic-charges-lines.svg';
import IconCalender from 'src/assets/images/icons/filter-calendar.svg';
import IconTradeLane from 'src/assets/images/icons/filter-trade-lane.svg';
import IconShipment from 'src/assets/images/icons/filter-shipment.svg';
import { nameofFactory } from 'src/utils/nameofFactory';
import { beautifyEnum, SimpleFormatNumber } from 'src/utils/utility';
import { CardId } from './generalOverview/types';
import { CardTheme, ChartType, ICardAnalyticItemProps } from 'src/presentations/components/molecules/card/cardAnalytic/types';
import { CO2 } from 'src/presentations/components/atom/unit';

export interface Option {
  value: string;
  label: string;
}

export interface FilterDetail {
  name: string;
  label: string;
  options: Option[];
}

export interface FilterAccordionProps {
  label: string;
  name: string;
  icon: string;
  filtersDetails: FilterDetail[];
  onChangeFilterNew?(filter: Record<string, any>): void;
}

export const formatNumberChart = (value: number) =>
  new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(value);

export const renderLegType = (legType: string) => {
  switch (legType) {
    case 'LEG_ONE':
      return 'Leg 1';
    case 'LEG_THREE':
      return 'Leg 3';
    case 'LEG_TWO':
      return 'Leg 2';
    default:
      return legType;
  }
};

export const keyMetricsMapper = (
  keyMetricsResponse: KeyMetricsResponseDto,
  isError?: boolean,
  filteredResponse?: KeyMetricsResponseDto
): ICardAnalyticItemProps[] => {
  const errorText = <span style={{ fontSize: '12px' }}>Failed to Load</span>;

  return [
    {
      id: CardId.TOTAL_AMOUNT,
      count: isError ? errorText : SimpleFormatNumber(keyMetricsResponse?.keymetrics?.totalAmount?.afterTax),
      filteredCount: filteredResponse ? SimpleFormatNumber(filteredResponse?.keymetrics?.totalAmount?.afterTax) : undefined,
      title: 'Total Amount',
      icon: IconDollar,
      theme: CardTheme.ORANGE,
      chartType: ChartType.LINE,
      unit: keyMetricsResponse?.meta?.currency,
    },
    {
      id: CardId.TOTAL_CO2_EMISSIONS,
      count: isError ? errorText : SimpleFormatNumber(keyMetricsResponse?.keymetrics?.totalCo2Emissions),
      filteredCount: filteredResponse ? SimpleFormatNumber(filteredResponse?.keymetrics?.totalCo2Emissions) : undefined,
      title: (
        <>
          <CO2 /> Emissions
        </>
      ),
      icon: IconCloud,
      theme: CardTheme.GREEN,
      chartType: ChartType.LINE,
      unit: keyMetricsResponse?.meta?.co2EmissionUnit,
    },
    {
      id: CardId.CHARGABLE_WEIGHT,
      count: isError ? errorText : SimpleFormatNumber(keyMetricsResponse?.keymetrics?.chargeableWeight),
      filteredCount: filteredResponse ? SimpleFormatNumber(filteredResponse?.keymetrics?.chargeableWeight) : undefined,
      title: 'Charg. Weight',
      icon: IconWeight,
      theme: CardTheme.NEUTRAL,
      chartType: ChartType.LINE,
      unit: keyMetricsResponse?.meta?.chargeableWeightUnit,
    },
    {
      id: CardId.GROSS_WEIGHT,
      count: isError ? errorText : SimpleFormatNumber(keyMetricsResponse?.keymetrics?.grossWeight),
      filteredCount: filteredResponse ? SimpleFormatNumber(filteredResponse?.keymetrics?.grossWeight) : undefined,
      title: 'Gross Weight',
      icon: IconWeightBlue,
      theme: CardTheme.BLUE,
      chartType: ChartType.LINE,
      unit: keyMetricsResponse?.meta?.grossWeightUnit,
    },
    {
      id: CardId.VOLUME,
      count: isError ? errorText : SimpleFormatNumber(keyMetricsResponse?.keymetrics?.volume),
      filteredCount: filteredResponse ? SimpleFormatNumber(filteredResponse?.keymetrics?.volume) : undefined,
      title: 'Volume',
      icon: IconBox,
      theme: CardTheme.BLUE_LIGHT,
      chartType: ChartType.LINE,
      unit: keyMetricsResponse?.meta?.volumeUnit === 'cbm' ? 'm³' : keyMetricsResponse?.meta?.volumeUnit,
    },
    {
      id: CardId.NO_SHIPMENTS,
      count: isError ? errorText : SimpleFormatNumber(keyMetricsResponse?.keymetrics?.noShipments),
      filteredCount: filteredResponse ? SimpleFormatNumber(filteredResponse?.keymetrics?.noShipments) : undefined,
      title: 'Shipments',
      icon: IconTruck,
      theme: CardTheme.PURPLE,
      chartType: ChartType.BAR,
      unit: '#',
    },
    {
      id: CardId.NO_INVOICES,
      count: isError ? errorText : SimpleFormatNumber(keyMetricsResponse?.keymetrics?.noInvoices),
      filteredCount: filteredResponse ? SimpleFormatNumber(filteredResponse?.keymetrics?.noInvoices) : undefined,
      title: 'Invoices',
      icon: IconReceipt,
      theme: CardTheme.PURPLE_LIGHT,
      chartType: ChartType.BAR,
      unit: '#',
    },
    {
      id: CardId.NO_CHARGES,
      count: isError ? errorText : SimpleFormatNumber(keyMetricsResponse?.keymetrics?.noCharges),
      filteredCount: filteredResponse ? SimpleFormatNumber(filteredResponse?.keymetrics?.noCharges) : undefined,
      title: 'Charges Lines',
      icon: IconChargesLines,
      theme: CardTheme.TEAL,
      chartType: ChartType.BAR,
      unit: '#',
    },
  ];
};

export const colorMapper = (theme: string) => {
  switch (theme) {
    case CardTheme.NEUTRAL:
      return '#A9B2C9';
    case CardTheme.ORANGE:
      return '#F79824';
    case CardTheme.GREEN:
      return '#3DA264';
    case CardTheme.BLUE:
      return '#4879F6';
    case CardTheme.BLUE_LIGHT:
      return '#5A9CFF';
    case CardTheme.PURPLE:
      return '#A375B8';
    case CardTheme.PURPLE_LIGHT:
      return '#CB6FF6';
    case CardTheme.PINK:
      return '#ED96DA';
    case CardTheme.TEAL:
      return '#16AD89';
    default:
      return '#D3D7DF';
  }
};

const nameof = nameofFactory<AnalyticSearchRequestDto>();
const nameofFilters = nameofFactory<FiltersDto>();

export const dateFiltersMapper = (dateFilters: DateFiltersDto) => [
  {
    name: `${nameof('filters')}.${nameofFilters('dateFilters')}.${nameofFactory<DateFiltersDto>()('years')}`,
    label: 'Year',
    options: dateFilters?.years.map((value) => ({
      value,
      label: value,
    })),
  },
  {
    name: `${nameof('filters')}.${nameofFilters('dateFilters')}.${nameofFactory<DateFiltersDto>()('quarters')}`,
    label: 'Quarter',
    options: dateFilters?.quarters.map((value) => ({
      value,
      label: value,
    })),
  },
  {
    name: `${nameof('filters')}.${nameofFilters('dateFilters')}.${nameofFactory<DateFiltersDto>()('months')}`,
    label: 'Month',
    options: dateFilters?.months.map((value) => ({
      value,
      label: value,
    })),
  },
  {
    name: `${nameof('filters')}.${nameofFilters('dateFilters')}.${nameofFactory<DateFiltersDto>()('weeks')}`,
    label: 'Week',
    options: dateFilters?.weeks.map((value) => ({
      value,
      label: value,
    })),
  },
  {
    name: `${nameof('filters')}.${nameofFilters('dateFilters')}.${nameofFactory<DateFiltersDto>()('days')}`,
    label: 'Day',
    options: dateFilters?.days.map((value) => ({
      value,
      label: value,
    })),
  },
];

export const shipmentFiltersMapper = (shipmentFilters: ShipmentFiltersDto) => [
  {
    name: `${nameof('filters')}.${nameofFilters('shipmentFilters')}.${nameofFactory<ShipmentFiltersDto>()('serviceProviderCode')}`,
    label: 'Service Provider',
    options: shipmentFilters?.serviceProviderCode
      .map((value) => ({
        value,
        label: beautifyEnum(value).toUpperCase(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    name: `${nameof('filters')}.${nameofFilters('shipmentFilters')}.${nameofFactory<ShipmentFiltersDto>()('modality')}`,
    label: 'Modality',
    options: shipmentFilters?.modality
      .map((value) => ({
        value,
        label: value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    name: `${nameof('filters')}.${nameofFilters('shipmentFilters')}.${nameofFactory<ShipmentFiltersDto>()('vehicleID')}`,
    label: 'Vehicle ID',
    options: shipmentFilters?.vehicleID
      .map((value) => ({
        value,
        label: value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  },
];

export const tradeLaneFiltersMapper = (tradeLaneFilters: TradeLaneFiltersDto) => [
  {
    name: `${nameof('filters')}.${nameofFilters('tradeLaneFilters')}.${nameofFactory<TradeLaneFiltersDto>()('tradeLaneCompany')}`,
    label: 'Shipper <> Consignee',
    options: tradeLaneFilters?.tradeLaneCompany
      .map((value) => ({
        value,
        label: value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    name: `${nameof('filters')}.${nameofFilters('tradeLaneFilters')}.${nameofFactory<TradeLaneFiltersDto>()('tradeLaneCityHub')}`,
    label: 'POL <> POD',
    options: tradeLaneFilters?.tradeLaneCityHub
      .map((value) => ({
        value,
        label: value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  },
  {
    name: `${nameof('filters')}.${nameofFilters('tradeLaneFilters')}.${nameofFactory<TradeLaneFiltersDto>()('tradeLaneCountry')}`,
    label: 'Orig.Country <> Dest. Country',
    options: tradeLaneFilters?.tradeLaneCountry
      .map((value) => ({
        value,
        label: value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  },
];

export const filterAccordionsMapper = (filterData: FiltersDto): FilterAccordionProps[] => [
  {
    label: 'Date',
    name: nameofFilters('dateFilters'),
    icon: IconCalender,
    filtersDetails: dateFiltersMapper(filterData?.dateFilters),
  },
  {
    label: 'Trade Lane',
    name: nameofFilters('tradeLaneFilters'),
    icon: IconTradeLane,
    filtersDetails: tradeLaneFiltersMapper(filterData?.tradeLaneFilters),
  },
  {
    label: 'Shipments Details',
    name: nameofFilters('shipmentFilters'),
    icon: IconShipment,
    filtersDetails: shipmentFiltersMapper(filterData?.shipmentFilters),
  },
];

export const filterChartGranularityMapper = (granularity: string) => {
  switch (granularity) {
    case 'YEARLY':
      return `${nameof('filters')}.${nameofFilters('dateFilters')}.${nameofFactory<DateFiltersDto>()('years')}`;
    case 'QUARTERLY':
      return `${nameof('filters')}.${nameofFilters('dateFilters')}.${nameofFactory<DateFiltersDto>()('quarters')}`;
    case 'MONTHLY':
      return `${nameof('filters')}.${nameofFilters('dateFilters')}.${nameofFactory<DateFiltersDto>()('months')}`;
    case 'WEEKLY':
      return `${nameof('filters')}.${nameofFilters('dateFilters')}.${nameofFactory<DateFiltersDto>()('weeks')}`;
    case 'DAILY':
      return `${nameof('filters')}.${nameofFilters('dateFilters')}.${nameofFactory<DateFiltersDto>()('days')}`;
    case 'COMPANY':
      return `${nameof('filters')}.${nameofFilters('tradeLaneFilters')}.${nameofFactory<TradeLaneFiltersDto>()('tradeLaneCompany')}`;
    case 'CITY_HUB':
      return `${nameof('filters')}.${nameofFilters('tradeLaneFilters')}.${nameofFactory<TradeLaneFiltersDto>()('tradeLaneCityHub')}`;
    case 'COUNTRY':
      return `${nameof('filters')}.${nameofFilters('tradeLaneFilters')}.${nameofFactory<TradeLaneFiltersDto>()('tradeLaneCountry')}`;
    case 'SERVICE_PROVIDER_NAME':
    case 'SERVICE_PROVIDER_CODE':
      return `${nameof('filters')}.${nameofFilters('shipmentFilters')}.${nameofFactory<ShipmentFiltersDto>()('serviceProviderCode')}`;
    case 'MODALITY':
      return `${nameof('filters')}.${nameofFilters('shipmentFilters')}.${nameofFactory<ShipmentFiltersDto>()('modality')}`;
    case 'VEHICLE_ID':
      return `${nameof('filters')}.${nameofFilters('shipmentFilters')}.${nameofFactory<ShipmentFiltersDto>()('vehicleID')}`;
    default:
      return null;
  }
};

export const countUnemptyField = (currentFilter: object) =>
  currentFilter ? Object.keys(currentFilter)?.filter((key) => currentFilter[key] !== undefined && currentFilter[key]?.length > 0).length : 0;

export const granularityMapper = (granularity: AnalyticSearchRequestDto['granularity']) => {
  switch (granularity) {
    case 'CHARGE_DOMAIN':
      return 'Charge Domain';
    case 'CITY_HUB':
      return 'POL <> POD';
    case 'COMPANY':
      return 'Shipper <> Consignee';
    case 'LEG_TYPE':
      return 'Leg Type';
    case 'COUNTRY':
      return 'Orig.Country <> Dest. Country';
    case 'DAILY':
      return 'Daily';
    case 'MONTHLY':
      return 'Monthly';
    case 'QUARTERLY':
      return 'Quarterly';
    case 'YEARLY':
      return 'Yearly';
    case 'WEEKLY':
      return 'Weekly';
    case 'SERVICE_PROVIDER_NAME':
      return 'Service Provider';
    case 'SERVICE_PROVIDER_CODE':
      return 'Service Provider';
    case 'MODALITY':
      return 'Modality';
    case 'VEHICLE_ID':
      return 'Vehicle Id';
    default:
      return '';
  }
};
